import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Download, AlertTriangle, DollarSign, Filter } from "lucide-react";

export default function SpendReport() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [vendor, setVendor] = useState("");
  const [department, setDepartment] = useState("");
  const [year, setYear] = useState("");
  const [totals, setTotals] = useState({ contracted: 0, actual: 0 });

  const load = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (vendor) params.set("vendor", vendor);
      if (department) params.set("department", department);
      if (year) params.set("year", year);
      const r = await api.get(`/reports/spend?${params}`);
      setRows(r.data.rows || []);
      setTotals({ contracted: r.data.total_contracted || 0, actual: r.data.total_actual || 0 });
    } catch (e) { toast.error("Load failed"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const exportCsv = async () => {
    const params = new URLSearchParams();
    if (vendor) params.set("vendor", vendor);
    if (department) params.set("department", department);
    if (year) params.set("year", year);
    params.set("format", "csv");
    try {
      const r = await api.get(`/reports/spend?${params}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "text/csv" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "spend_report.csv";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error("Export failed"); }
  };

  return (
    <div className="max-w-7xl mx-auto p-6" data-testid="spend-report">
      <div className="flex items-center gap-3 mb-6">
        <DollarSign className="w-6 h-6 text-[#005289]" />
        <div className="flex-1">
          <h1 className="text-xl font-bold">Spend Report</h1>
          <p className="text-sm text-slate-500">Actual spend by contract / vendor / department / year with flagged variances.</p>
        </div>
        <button onClick={exportCsv} className="h-9 px-4 bg-[#005289] text-white text-sm font-semibold rounded flex items-center gap-2" data-testid="spend-export-csv">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4 mb-4 flex items-end gap-3" data-testid="spend-filters">
        <div>
          <label className="text-xs font-bold block mb-1">Vendor contains</label>
          <input className="h-9 px-2 text-sm border border-slate-200 rounded" value={vendor} onChange={e => setVendor(e.target.value)} placeholder="Acme" data-testid="spend-filter-vendor" />
        </div>
        <div>
          <label className="text-xs font-bold block mb-1">Department</label>
          <input className="h-9 px-2 text-sm border border-slate-200 rounded" value={department} onChange={e => setDepartment(e.target.value)} placeholder="IT" data-testid="spend-filter-dept" />
        </div>
        <div>
          <label className="text-xs font-bold block mb-1">Contract Year</label>
          <input type="number" className="h-9 px-2 w-24 text-sm border border-slate-200 rounded" value={year} onChange={e => setYear(e.target.value)} placeholder="1" data-testid="spend-filter-year" />
        </div>
        <button disabled={busy} onClick={load} className="h-9 px-4 bg-slate-700 text-white text-sm rounded disabled:opacity-50" data-testid="spend-apply-filters">
          <Filter className="w-4 h-4 inline mr-1" /> Apply
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-white border border-slate-200 rounded p-3">
          <div className="text-[10px] uppercase text-slate-500">Rows</div>
          <div className="text-2xl font-bold">{rows.length}</div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
          <div className="text-[10px] uppercase text-emerald-700">Total contracted</div>
          <div className="text-2xl font-bold text-emerald-900">${totals.contracted.toLocaleString()}</div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded p-3">
          <div className="text-[10px] uppercase text-blue-700">Total actual</div>
          <div className="text-2xl font-bold text-blue-900">${totals.actual.toLocaleString()}</div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left p-2">Contract #</th>
              <th className="text-left p-2">Vendor</th>
              <th className="text-left p-2">Dept</th>
              <th className="text-left p-2">Year</th>
              <th className="text-left p-2">Period</th>
              <th className="text-left p-2">Contracted</th>
              <th className="text-left p-2">Actual</th>
              <th className="text-left p-2">Variance</th>
              <th className="text-left p-2">Flags</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => nav(`/contract/${r.contract_id}?tab=spend`)} data-testid={`spend-row-${r.contract_number}-y${r.year}`}>
                <td className="p-2 font-mono">#{r.contract_number}</td>
                <td className="p-2">{r.vendor}</td>
                <td className="p-2">{r.department}</td>
                <td className="p-2 font-semibold">Y{r.year}</td>
                <td className="p-2 text-slate-500">{r.year_start} → {r.year_end}</td>
                <td className="p-2">${(r.contracted || 0).toLocaleString()}</td>
                <td className="p-2">${(r.actual || 0).toLocaleString()}</td>
                <td className={`p-2 font-semibold ${r.variance > 0 ? "text-red-700" : "text-emerald-700"}`}>${r.variance.toLocaleString()}</td>
                <td className="p-2">
                  {r.flags ? r.flags.split(",").map(f => (
                    <span key={f} className="inline-block text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-800 mr-1">
                      <AlertTriangle className="w-2.5 h-2.5 inline mr-0.5" /> {f}
                    </span>
                  )) : <span className="text-slate-400">—</span>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && !busy && <tr><td colSpan={9} className="p-8 text-center text-slate-400">No spend data yet. Set contract schedules and attach invoices.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
